import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { widgetsApi } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash2, Edit, Code, ExternalLink } from 'lucide-react';
import type { Widget } from '../types';
import WidgetForm from '../components/WidgetForm';

export default function Widgets() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);

  const { data: widgetsData, isLoading } = useQuery({
    queryKey: ['widgets', organization?.id],
    queryFn: () => widgetsApi.list(organization!.id),
    enabled: !!organization,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => widgetsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => widgetsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      setSelectedWidget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => widgetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
    },
  });

  const widgets = widgetsData?.data?.data || [];

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this widget?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const getEmbedCode = (widgetId: string) => {
    return `<script>
  (function() {
    var script = document.createElement('script');
    script.src = 'https://ncai-chat.bill-burkey.workers.dev/widget.js';
    script.setAttribute('data-widget-id', '${widgetId}');
    script.async = true;
    document.head.appendChild(script);
  })();
</script>`;
  };

  const copyEmbedCode = (widgetId: string) => {
    navigator.clipboard.writeText(getEmbedCode(widgetId));
    alert('Embed code copied to clipboard!');
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Widgets</h1>
          <p className="text-gray-500 mt-2">Manage your AI chat widgets</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary px-4 py-2 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create Widget</span>
        </button>
      </div>

      {widgets.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No widgets yet</h3>
          <p className="text-gray-500 mb-6">Create your first widget to get started with AI chat</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary px-6 py-2"
          >
            Create Widget
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {widgets.map((widget: Widget) => (
            <div key={widget.id} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{widget.name}</h3>
                  <a
                    href={widget.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1 mt-1"
                  >
                    <span>{widget.websiteUrl}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  widget.status === 'active' ? 'bg-green-100 text-green-700' :
                  widget.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {widget.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Model:</span>
                  <span className="text-gray-900 font-medium">{widget.aiConfig.model.split('/').pop()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Position:</span>
                  <span className="text-gray-900 font-medium">{widget.widgetConfig.theme.position}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-gray-900 font-medium">
                    {new Date(widget.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => copyEmbedCode(widget.id)}
                  className="flex-1 btn btn-secondary px-3 py-2 text-sm flex items-center justify-center space-x-2"
                >
                  <Code className="w-4 h-4" />
                  <span>Embed Code</span>
                </button>
                <button
                  onClick={() => setSelectedWidget(widget)}
                  className="btn btn-secondary px-3 py-2"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(widget.id)}
                  className="btn btn-secondary px-3 py-2 text-red-600 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && organization && (
        <WidgetForm
          organizationId={organization.id}
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data);
          }}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {selectedWidget && organization && (
        <WidgetForm
          widget={selectedWidget}
          organizationId={organization.id}
          onSubmit={async (data) => {
            await updateMutation.mutateAsync({ id: selectedWidget.id, data });
          }}
          onClose={() => setSelectedWidget(null)}
        />
      )}
    </div>
  );
}
